import React from 'react';
import { Webhook } from 'lucide-react';
import { getStatusLabel, getSubstatusLabel } from '../../../../../features/webhooks/webhooksUtils';

const th = 'px-6 py-3 text-[11px] font-semibold text-text-muted uppercase tracking-wider text-left';
const td = 'px-6 py-4 text-sm text-ninja-dark dark:text-white align-top';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-CL') : '-');

/**
 * The header names the seller typed. The form stores its own placeholders
 * (`{{content_type}}`, `{{authorization}}`) next to them, and those say
 * nothing about what reaches the endpoint.
 */
const headerNames = (headers) =>
  Object.keys(headers || {}).filter((name) => !/^\{\{.*\}\}$/.test(name));

const fieldCount = (body) => Object.keys(body || {}).length;

/**
 * The webhooks a seller set up, read from its detail. A listing only: the
 * backend fires one per exact status + substatus pair, so the pair is what an
 * admin checks here when a seller says a notification never arrived.
 *
 * @param {object} props
 * @param {Array<object>} props.webhooks - `SettingWebhook` rows as the backend answers them.
 */
export const SellerWebhooksTab = ({ webhooks = [] }) => (
  <div
    data-testid="seller-webhooks-tab"
    className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300"
  >
    <div className="p-4 border-b border-border flex items-center justify-between gap-4">
      <h3 className="text-base font-bold text-ninja-dark dark:text-white flex items-center gap-2">
        <Webhook size={18} className="text-text-muted" />
        Webhooks configurados ({webhooks.length})
      </h3>
      <span className="text-xs text-text-muted">Los administra el seller desde su cuenta.</span>
    </div>

    {webhooks.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-ninja-gray dark:bg-[#0f0f0f]">
            <tr>
              <th className={th}>Estado</th>
              <th className={th}>Subestado</th>
              <th className={th}>URL destino</th>
              <th className={th}>Headers</th>
              <th className={th}>Campos del body</th>
              <th className={th}>Creado</th>
            </tr>
          </thead>
          <tbody>
            {webhooks.map((webhook) => {
              const names = headerNames(webhook.headers);
              const fields = fieldCount(webhook.body);
              return (
                <tr
                  key={webhook.id}
                  data-testid="seller-webhook-row"
                  data-status={webhook.status}
                  data-substatus={webhook.substatus}
                  className="border-t border-border"
                >
                  <td className={`${td} font-bold`}>{getStatusLabel(webhook.status)}</td>
                  <td className={td}>{getSubstatusLabel(webhook.status, webhook.substatus)}</td>
                  <td className={td}>
                    <span className="block max-w-[360px] truncate text-text-secondary" title={webhook.url}>
                      {webhook.url}
                    </span>
                  </td>
                  <td className={td}>
                    {names.length > 0 ? (
                      <span className="flex flex-wrap gap-1">
                        {names.map((name) => (
                          <span
                            key={name}
                            className="px-2 py-0.5 bg-ninja-gray dark:bg-[#2d2d2d] rounded-md text-xs font-mono"
                          >
                            {name}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-text-muted">-</span>
                    )}
                  </td>
                  <td className={td} title={Object.keys(webhook.body || {}).join(', ')}>
                    {fields > 0 ? fields : <span className="text-text-muted">-</span>}
                  </td>
                  <td className={td}>{formatDate(webhook.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="p-6 text-sm text-text-secondary">
        Este seller no tiene webhooks configurados.
      </p>
    )}
  </div>
);
